/**
 * ClientState: the one object the client packages share. Owns the socket
 * messages' effects (room roster, snapshot buffer, clock sync), the local
 * player's prediction (movement + queue, DESIGN 5.6) and produces the
 * ViewModel each frame.
 *
 * The public surface below is what WP6 (render/HUD) and WP7 (UI/audio) code
 * against. Wiring: `socket.onAny((m) => state.onServerMsg(m))`, then per
 * frame `controls.update(dt); const msg = state.flushInput(); if (msg)
 * socket.send(msg); const vm = state.frame(now);`.
 *
 * No DOM access: the only browser global (localStorage, for the reconnect
 * token) lives behind sessionToken.ts, so the class runs unchanged in node.
 */

import type {
  Action,
  Arena,
  ClientMsg,
  Element,
  ErrCode,
  GameEvent,
  LobbyPlayer,
  MatchResult,
  MoveInput,
  PlayerSnap,
  RoomPhase,
  ServerMsg,
  SnapMsg,
  SpellSpec,
  Vec2,
} from '@shared/index.js';
import type { ModeId, TerrainSnap } from '@shared/index.js';
import { matchGrand, weaveTarget } from '@shared/index.js';
import {
  ARENA,
  DEFAULT_MODE,
  PLAYER_RADIUS,
  chargeFromHold,
  resolveCast,
  INTERP_DELAY_MS,
  MODES,
  TICK_DT,
  isPvp,
  MAX_ACTS_PER_INPUT,
  NET,
  PLAYER_HP,
  SNAP_EVERY,
  TICK_RATE,
  arenaFor,
  matchMagick,
  pushElement,
  stepMovement,
  withTerrain,
} from '@shared/index.js';
import type { ChatLine, LocalView, QueueFeedback, ViewModel, ViewPlayer, ViewRoom } from './viewModel.js';
import { ServerClock } from '../net/clock.js';
import { SnapshotBuffer } from '../net/snapshotBuffer.js';
import { Prediction } from './prediction.js';
import { interpolateEntities, interpolateMarkers, spellViews } from './interpolation.js';
import { PreviewCache } from './preview.js';
import { BeamSmoother, beamTurnMul } from './beamSmoothing.js';
import { RockPredictor } from './rockPrediction.js';
import { defaultStorage, loadToken, storeToken } from './sessionToken.js';
import type { StorageLike } from './sessionToken.js';

export type { StorageLike };
export type ViewListener = (vm: ViewModel) => void;

export interface ClientStateOptions {
  /** Local ms clock; defaults to performance.now(). Inject a fake in tests. */
  now?: () => number;
  /** Token storage; defaults to window.localStorage when present, null in node. */
  storage?: StorageLike | null;
}

/** Chat lines kept in the ViewModel. */
const CHAT_HISTORY = 200;
/** Minimum ms between input frames: at most NET.clientInputHz per second (DESIGN 5.2). */
const INPUT_GAP_MS = 1000 / NET.clientInputHz;
/** A hidden tab stops rAF; cap the prediction step so one input does not cover seconds. */
const MAX_INPUT_DT = 0.25;
/** Preview sub-step between commits never exceeds one input gap (plus a little slack). */
const MAX_PREVIEW_DT = (INPUT_GAP_MS * 2) / 1000;
const FPS_ALPHA = 0.1;

/** A ViewModel with nothing in it: the state before any server message. */
export function emptyViewModel(): ViewModel {
  return {
    epoch: 0,
    renderTime: 0,
    serverTime: 0,
    rtt: 0,
    fps: 0,
    phase: 'lobby',
    room: null,
    me: null,
    players: [],
    enemies: [],
    projectiles: [],
    beams: [],
    sprays: [],
    arcs: [],
    hazards: [],
    markers: [],
    corpses: [],
    wave: null,
    terrain: null,
    ctf: null,
    story: null,
    events: [],
    chat: [],
  };
}

function defaultNow(): number {
  const perf = (globalThis as { performance?: { now(): number } }).performance;
  return perf ? perf.now() : Date.now();
}

export class ClientState {
  /** Our player id from `welcome`; -1 until then. */
  pid = -1;
  /** Reconnect token from `welcome` (DESIGN 5.7); on construction, a token stored by a recent page load. */
  token: string | null = null;
  /** Current room code, null outside a room. */
  roomCode: string | null = null;
  phase: RoomPhase = 'lobby';
  /** Newest snapshot as received (authoritative; the ViewModel is derived from it). */
  lastSnap: SnapMsg | null = null;
  /**
   * The map the room plays as it stands now: the `room` frame's level with
   * the newest snapshot's open doors and ice applied (level.ts withTerrain).
   * Prediction, the camera and the renderer use it.
   */
  arena: Arena = ARENA;
  /** The room's level as compiled (every door as the level sets it, no ice). */
  baseArena: Arena = ARENA;
  /** What the room plays (the `room` frame's settings). */
  mode: ModeId = DEFAULT_MODE;
  /** The server tick the last frame drew other players at (PvP inputs carry it); -1 before any snapshot. */
  private viewTick = -1;
  /** Counts every time the picture starts over (ViewModel.epoch). */
  private epoch = 0;
  /** The terrain the current `arena` was built from, as a comparable key. */
  private terrainKey = '';
  /** The ViewModel produced by the most recent frame(). */
  viewModel: ViewModel = emptyViewModel();

  /** Server time estimate (DESIGN 5.6 clock sync). */
  readonly clock = new ServerClock();
  /** The last 32 snapshots, for interpolation. */
  readonly buffer = new SnapshotBuffer();
  /** Local movement and queue prediction. */
  readonly prediction: Prediction;
  /** Roster from the last `room` message; same object until the next one, so UI can diff by identity. */
  room: ViewRoom | null = null;
  /** Chat history, oldest first; a new array per line so UI can diff by identity. */
  chat: ChatLine[] = [];
  /** Last `err` frame, for the landing page / toasts. */
  lastError: { code: ErrCode; msg: string; at: number } | null = null;
  /** Last `end` frame, for the results overlay. Cleared when a new room arrives. */
  lastResult: MatchResult | null = null;
  tickRate = TICK_RATE;
  snapEvery = SNAP_EVERY;

  private _localName = '';
  private _localColor = 0;
  private readonly now: () => number;
  private readonly storage: StorageLike | null;
  private readonly listeners = new Set<ViewListener>();
  private readonly roster = new Map<number, LobbyPlayer>();
  private readonly deadSince = new Map<number, number>();
  private readonly beamSeen = new Map<number, number>();
  private readonly previews = new PreviewCache();
  private pendingEvents: GameEvent[] = [];
  private _localSnap: PlayerSnap | null = null;
  private mv: MoveInput = { k: 's' };
  private _aim = 0;
  private seq = 0;
  private lastFlushAt = Number.NEGATIVE_INFINITY;
  private lastFrameAt = Number.NEGATIVE_INFINITY;
  private fps = 0;
  private queueFeedback: QueueFeedback | null = null;

  constructor(opts: ClientStateOptions = {}) {
    this.now = opts.now ?? defaultNow;
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage;
    this.prediction = new Prediction({ step: stepMovement, push: pushElement, matchMagick });
    this.token = loadToken(this.storage);
  }

  /** Name the player typed on the landing page (remembered in localStorage by WP7). */
  get localName(): string { return this._localName; }
  set localName(name: string) { this._localName = name; }

  /** Robe colour index 0..3 (DESIGN 6.3). */
  get localColor(): number { return this._localColor; }
  set localColor(color: number) { this._localColor = color; }

  /** The predicted queue (what the HUD shows). */
  get queue(): readonly Element[] { return this.prediction.queue; }
  /** Latest aim angle in radians. */
  get aim(): number { return this._aim; }
  /** Our PlayerSnap from the newest snapshot, null before the first one. */
  get localSnap(): PlayerSnap | null { return this._localSnap; }
  get inRoom(): boolean { return this.roomCode !== null; }
  /** The server has frozen the match for our open menu (we are the room's only player). */
  get paused(): boolean { return this.room?.paused === true; }

  /** Predicted (blended) world position of the local player, null before the first snapshot that includes us. */
  predictedPos(now: number = this.now()): Vec2 | null {
    if (!this._localSnap) return null;
    return this.prediction.renderPos(now, this.mv, this.previewDt(now));
  }

  /** Feed every ServerMsg here: welcome/room/snap/pong/end/err/chat. */
  onServerMsg(msg: ServerMsg): void {
    switch (msg.t) {
      case 'welcome': this.onWelcome(msg); break;
      case 'room': this.onRoom(msg); break;
      case 'snap': this.onSnap(msg); break;
      case 'pong': this.clock.onPong(msg.c, msg.st, this.now()); break;
      case 'end':
        this.lastResult = msg.result;
        this.phase = 'gameover';
        break;
      case 'err':
        this.lastError = { code: msg.code, msg: msg.msg, at: this.now() };
        // DESIGN 5.7: the session is gone; the UI returns to the landing page.
        if (msg.code === 'no_room' || msg.code === 'room_crashed') this.leaveRoom();
        break;
      case 'chat':
        this.chat = [...this.chat.slice(-(CHAT_HISTORY - 1)), { from: msg.name, text: msg.text, t: msg.st }];
        break;
    }
  }

  /**
   * Queue an Action for the next input frame. Element presses are also
   * applied to the predicted queue immediately (DESIGN 5.6).
   */
  queueAction(act: Action): void {
    const now = this.now();
    if (act.a === 'cast' && act.m === 'force') act = this.throwing(act, now);
    const fb = this.prediction.act(act, now);
    if (fb) this.queueFeedback = { outcome: fb.outcome, el: fb.el, at: now };
  }

  /**
   * Force with a projectile queued starts our own charge at once (the rock grows in the hand without waiting for the
   * server); letting go stamps the release with how long it was held (`h`), which the server throws with, and the
   * predicted rock leaves when that release is sent (flushInput).
   */
  private throwing(act: Extract<Action, { a: 'cast' }>, now: number): Action {
    if (act.ph === 'b') {
      if (this.charging || this.releasing || !this.prediction.canForce(now)) return act;
      const spec = resolveCast(this.prediction.queue, 'force');
      if (spec.kind === 'projectile') this.charging = { spec, at: now };
      return act;
    }
    const c = this.charging;
    if (!c) return act;
    this.charging = null;
    const out = { ...act, h: Math.round(Math.max(0, now - c.at)) / 1000 };
    this.releasing = { act: out, spec: c.spec };
    return out;
  }

  /** Our charge 0..1 right now, or null when not holding a projectile. */
  private chargeNow(now: number): number | null {
    if (this.releasing) return chargeFromHold(this.releasing.act.h ?? 0);
    if (this.charging) return chargeFromHold((now - this.charging.at) / 1000);
    return null;
  }

  /** Expected ms between sending a release and seeing the server's result: a round trip plus the waits for a tick and a snapshot. */
  private throwLatencyMs(): number {
    const tickMs = 1000 / this.tickRate;
    return this.clock.rtt + tickMs / 2 + (tickMs * this.snapEvery) / 2;
  }

  /** Latest movement intent from Controls (RMB target, arrow direction or stop). */
  setMove(mv: MoveInput): void {
    this.mv = mv;
  }

  /** Latest aim angle (radians) from the cursor. */
  setAim(aim: number): void {
    if (Number.isFinite(aim)) this._aim = aim;
  }

  /**
   * Build the coalesced `input` message for this client frame (at most
   * NET.clientInputHz per second, DESIGN 5.2) and advance prediction.
   * Returns null when there is nothing to send: no room, or rate limited
   * (pending actions wait for the next frame; the local prediction already
   * showed their effect).
   */
  flushInput(now: number = this.now()): ClientMsg | null {
    if (this.roomCode === null) return null;
    if (now - this.lastFlushAt < INPUT_GAP_MS) return null;
    // Paused (the server has frozen the match): nobody moves, us included, so the prediction holds still too.
    const dt = Number.isFinite(this.lastFlushAt) && !this.paused ? Math.min((now - this.lastFlushAt) / 1000, MAX_INPUT_DT) : 0;
    this.lastFlushAt = now;
    const seq = ++this.seq;
    const acts = this.prediction.commit(seq, this.mv, dt, MAX_ACTS_PER_INPUT);
    const rel = this.releasing;
    if (rel && acts.includes(rel.act)) {
      // The rock leaves now, from where the server will have us after this input's movement, along the release's aim.
      this.releasing = null;
      this.releaseSeq = seq;
      if (rel.spec.kind === 'projectile' && rel.spec.solid === 'earth' && !this.prediction.dead) {
        this.rocks.launch(now, this.prediction.pos, rel.act.aim, PLAYER_RADIUS, rel.spec.range, chargeFromHold(rel.act.h ?? 0), rel.spec.elements, this.throwLatencyMs(), this.arena);
      }
    }
    // PvP: tell the server which tick we are drawing the others at (it rewinds beams and arcs to it) and our ping
    if (isPvp(this.mode) && this.viewTick >= 0) {
      return { t: 'input', seq, mv: this.mv, aim: this._aim, acts, dt, vt: Math.round(this.viewTick * 100) / 100, rtt: Math.round(this.clock.rtt) };
    }
    return { t: 'input', seq, mv: this.mv, aim: this._aim, acts, dt };
  }

  /**
   * Produce the ViewModel for render time `now` (performance.now() ms):
   * interpolate remote entities, apply local prediction, drain events.
   * Stores the result in `viewModel` and notifies subscribers.
   */
  frame(now: number): ViewModel {
    this.tickFps(now);
    const serverTime = this.clock.synced ? this.clock.now(now) : 0;
    // PvP modes draw the others closer behind (they also get a snapshot every tick)
    const renderTime = serverTime - (MODES[this.mode]?.interpMs ?? INTERP_DELAY_MS);
    // the server tick that render time shows, for the inputs (PvP rewind)
    const latest = this.buffer.newest;
    this.viewTick = latest ? Math.max(0, latest.tick - (latest.st - renderTime) / (TICK_DT * 1000)) : -1;
    const vm = emptyViewModel();
    vm.epoch = this.epoch;
    vm.renderTime = renderTime;
    vm.serverTime = serverTime;
    vm.rtt = this.clock.rtt;
    vm.fps = this.fps;
    vm.phase = this.phase;
    vm.room = this.room;

    const ents = interpolateEntities(this.buffer, renderTime, { pid: this.pid, roster: this.roster, deadSince: this.deadSince });
    const localPos = this.applyLocalPose(ents.players, now);
    vm.players = ents.players;
    vm.enemies = ents.enemies;
    // our own thrown rocks are drawn from the prediction; their server twins stay hidden
    const hidden = this.rocks.hidden(now);
    vm.projectiles = hidden.size > 0 ? ents.projectiles.filter((p) => !hidden.has(p.id)) : ents.projectiles;
    vm.projectiles.push(...this.rocks.views(now, this.pid));
    vm.corpses = ents.corpses;

    const newest = this.buffer.newest;
    const spells = spellViews(newest, this.pid, localPos
      ? { pos: localPos, aim: this._aim, beamSince: this.beamSeen, serverTime }
      : null);
    // our spray comes out of where we are drawn, toward where we aim now (the cone the server hits with follows the aim)
    if (localPos) {
      for (const s of spells.sprays) {
        if (s.o !== this.pid) continue;
        s.rx = localPos.x;
        s.ry = localPos.y;
        s.facing = this._aim;
      }
    }
    vm.beams = spells.beams;
    const frameDt = Number.isFinite(this.beamFrameAt) ? Math.min(0.1, Math.max(0, (now - this.beamFrameAt) / 1000)) : 0;
    this.beamFrameAt = now;
    const mine = this._localSnap;
    this.beamSmoother.apply(vm.beams, vm.players, mine ? { pid: this.pid, aim: this._aim, turnMul: beamTurnMul(mine.st.map((x) => x.id)) } : null, frameDt);
    vm.sprays = spells.sprays;
    vm.arcs = spells.arcs;
    vm.hazards = spells.hazards;
    vm.markers = interpolateMarkers(this.buffer, renderTime, this.pid);
    vm.wave = newest?.w ?? null;
    vm.terrain = newest?.tr ?? null;
    vm.story = newest?.sy ?? null;
    vm.ctf = newest?.ctf ?? null;
    vm.me = this._localSnap ? this.localView(this._localSnap, localPos, now) : null;

    vm.events = this.pendingEvents;
    this.pendingEvents = [];
    vm.chat = this.chat;

    this.viewModel = vm;
    for (const fn of this.listeners) fn(vm);
    return vm;
  }

  /** Called after every frame() with the fresh ViewModel. Returns an unsubscribe function. */
  subscribe(fn: ViewListener): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  /**
   * The page is going away (refresh, navigation; main.ts calls this on pagehide).
   * The stored token is stamped at `welcome`, so without this a refresh more than
   * RECONNECT_SECONDS after joining offered no token: the player came back as a
   * stranger (dead until the breather mid-wave, or `room_full` in a full room)
   * while their old wizard sat out its reconnect window. Re-stamping now lets a
   * reload inside the window rebind to the same player (DESIGN 5.7). Only in a
   * room: outside one the token would just earn a `no_room`.
   */
  rememberSession(nowMs: number = Date.now()): void {
    if (this.token === null || this.roomCode === null) return;
    storeToken(this.storage, this.token, nowMs);
  }

  /** Forget the room (after `leave`, `no_room` or `room_crashed`). The UI sends the `leave` frame itself. */
  leaveRoom(): void {
    this.roomCode = null;
    this.room = null;
    this.roster.clear();
    this.phase = 'lobby';
    this.resetRoomState();
  }

  // ------------------------------------------------------------------ message handlers

  private onWelcome(msg: Extract<ServerMsg, { t: 'welcome' }>): void {
    this.pid = msg.pid;
    this.token = msg.token;
    this.tickRate = msg.tickRate;
    this.snapEvery = msg.snapEvery;
    storeToken(this.storage, msg.token);
    // A fresh socket may be talking to a restarted server with a new clock.
    this.clock.reset();
    this.buffer.clear();
    this.epoch++;
  }

  private onRoom(msg: Extract<ServerMsg, { t: 'room' }>): void {
    const newRoom = msg.code !== this.roomCode;
    this.roomCode = msg.code;
    this.phase = msg.phase;
    this.mode = msg.settings.mode ?? DEFAULT_MODE;
    this.room = { code: msg.code, players: msg.players, host: msg.host, mode: this.mode, paused: msg.paused === 1, difficulty: msg.settings.difficulty ?? 'normal', ...(msg.wait ? { wait: msg.wait } : {}) };
    this.roster.clear();
    for (const p of msg.players) this.roster.set(p.id, p);
    const base = arenaFor(msg.level);
    if (base !== this.baseArena) {
      this.baseArena = base;
      this.terrainKey = '';
      this.setArena(base);
    }
    if (newRoom) this.resetRoomState();
    this.prediction.pvp = isPvp(this.mode);
  }

  private setArena(arena: Arena): void {
    if (arena === this.arena) return;
    this.arena = arena;
    this.prediction.setArena(arena);
  }

  /** Story maps: rebuild the map when a snapshot's doors, ice, ash, stone or flood differ from the last ones. */
  private applyTerrain(tr: TerrainSnap | undefined): void {
    const key = tr ? `${tr.d.join(',')}|${tr.i.join(',')}|${(tr.a ?? []).join(',')}|${(tr.s ?? []).join(',')}|${(tr.f ?? []).join(',')}` : '';
    if (key === this.terrainKey) return;
    this.terrainKey = key;
    this.setArena(tr
      ? withTerrain(this.baseArena, { open: new Set(tr.d), frozen: new Set(tr.i), ash: new Set(tr.a ?? []), filled: new Set(tr.s ?? []), flooded: new Set(tr.f ?? []) })
      : this.baseArena);
  }

  private onSnap(msg: SnapMsg): void {
    const now = this.now();
    if (!this.clock.synced) this.clock.seed(msg.st, now);
    if (!this.buffer.push(msg)) {
      // Older than the newest we hold: the server clock went backwards (new
      // process after a reconnect). Start over from this snapshot.
      this.buffer.clear();
      this.clock.reset();
      this.clock.seed(msg.st, now);
      this.buffer.push(msg);
    }
    this.lastSnap = msg;
    this.lastAck = msg.ack;
    this.rocks.onSnap(msg.pr, this.pid, now);
    this.phase = msg.w.phase;
    this.applyTerrain(msg.tr);
    for (const ev of msg.ev) this.pendingEvents.push(ev);
    this.trackDeaths(msg);
    this.trackBeams(msg);

    const me = msg.p.find((p) => p.id === this.pid) ?? null;
    this._localSnap = me;
    if (me) {
      // PvP: a Revive only spends the cooldown when someone on our team is down (spells/caster.ts)
      this.prediction.reviveTarget = msg.p.some((p) => p.dead === 1 && p.id !== this.pid && p.tm === me.tm);
      const r = this.prediction.reconcile(me, msg.ack, now);
      if (r === 'fizzle') this.queueFeedback = { outcome: 'fizzle', el: null, at: now };
    }
    // DESIGN 5.6: self_shock is only trusted from the server.
    for (const ev of msg.ev) {
      if (ev.e === 'shock_self' && ev.p === this.pid) this.queueFeedback = { outcome: 'self_shock', el: 'lightning', at: now };
    }
  }

  private trackDeaths(msg: SnapMsg): void {
    for (const p of msg.p) {
      if (p.dead === 1) {
        if (!this.deadSince.has(p.id)) this.deadSince.set(p.id, msg.st);
      } else {
        this.deadSince.delete(p.id);
      }
    }
  }

  private trackBeams(msg: SnapMsg): void {
    const present = new Set<number>();
    for (const b of msg.b) {
      present.add(b.id);
      if (!this.beamSeen.has(b.id)) this.beamSeen.set(b.id, msg.st);
    }
    for (const id of this.beamSeen.keys()) if (!present.has(id)) this.beamSeen.delete(id);
  }

  private resetRoomState(): void {
    this.epoch++;
    this.buffer.clear();
    this.prediction.reset();
    this.previews.clear();
    this.deadSince.clear();
    this.beamSeen.clear();
    this.beamSmoother.clear();
    this.rocks.clear();
    this.charging = null;
    this.releasing = null;
    this.releaseSeq = -1;
    this.lastAck = 0;
    this.pendingEvents = [];
    this.chat = [];
    this.lastSnap = null;
    this._localSnap = null;
    this.lastResult = null;
    this.queueFeedback = null;
    this.mv = { k: 's' };
    this.seq = 0;
    this.lastFlushAt = Number.NEGATIVE_INFINITY;
  }

  // ------------------------------------------------------------------ frame helpers

  private readonly beamSmoother = new BeamSmoother();
  private beamFrameAt = Number.NEGATIVE_INFINITY;
  /** Our thrown rocks, predicted (rockPrediction.ts). */
  private readonly rocks = new RockPredictor();
  /** Holding Force with a projectile: the spell and the local ms the hold began. */
  private charging: { spec: SpellSpec; at: number } | null = null;
  /** Let go, waiting for the next input frame to carry the release (the rock leaves then). */
  private releasing: { act: Extract<Action, { a: 'cast' }>; spec: SpellSpec } | null = null;
  /** The input seq that carried our last release, and the newest ack: until it is acked the server still shows the charge. */
  private releaseSeq = -1;
  private lastAck = 0;

  private tickFps(now: number): void {
    if (Number.isFinite(this.lastFrameAt)) {
      const dtMs = now - this.lastFrameAt;
      if (dtMs > 0) {
        const inst = 1000 / dtMs;
        this.fps = this.fps === 0 ? inst : this.fps + FPS_ALPHA * (inst - this.fps);
      }
    }
    this.lastFrameAt = now;
  }

  /** Seconds since the last input commit, for the smooth preview sub-step. */
  private previewDt(now: number): number {
    if (!Number.isFinite(this.lastFlushAt) || this.paused) return 0;
    return Math.min(Math.max(0, (now - this.lastFlushAt) / 1000), MAX_PREVIEW_DT);
  }

  /**
   * Replace the local player's interpolated pose with the prediction (the
   * newest snapshot's fields for hp/statuses, the predicted queue, the local
   * aim as facing). Dead players keep the server pose. Returns the position
   * used, or null when we are not in the snapshot yet.
   */
  private applyLocalPose(players: ViewPlayer[], now: number): Vec2 | null {
    const snap = this._localSnap;
    if (!snap) return null;
    const idx = players.findIndex((p) => p.isLocal);
    if (idx < 0) return null;
    const cur = players[idx] as ViewPlayer;
    if (this.prediction.dead) {
      this.charging = null;
      return { x: cur.rx, y: cur.ry };
    }
    const pos = this.prediction.renderPos(now, this.mv, this.previewDt(now));
    const view: ViewPlayer = { ...cur, ...snap, rx: pos.x, ry: pos.y, facing: this._aim, q: this.prediction.queue, isLocal: true };
    // our charge is our own: it shows from the press, and stops showing at the release, whatever the server has caught up to
    const charge = this.chargeNow(now);
    const held = this.charging?.spec ?? this.releasing?.spec;
    if (charge !== null) {
      view.act = 'charge';
      view.ch = charge;
      if (held && 'elements' in held) view.che = held.elements;
    } else if (view.act === 'charge' && this.lastAck < this.releaseSeq) {
      view.act = 'cast';
      view.ch = 0;
    }
    players[idx] = view;
    return pos;
  }

  private localView(snap: PlayerSnap, pos: Vec2 | null, now: number): LocalView {
    const queue = this.prediction.queue;
    const { preview, previewName, magickHint } = this.previews.for(queue);
    const magickCd = this.prediction.magickCooldown(now);
    const view: LocalView = {
      pid: this.pid,
      queue,
      preview,
      previewName,
      hp: snap.hp,
      maxHp: PLAYER_HP,
      statuses: snap.st,
      charge: this.chargeNow(now) ?? snap.ch,
      magickHint,
      dead: snap.dead === 1,
      wet: this.prediction.wet,
      ...(snap.imb && snap.imc ? { imb: snap.imb, imc: snap.imc } : {}),
      aim: this._aim,
    };
    if (this.queueFeedback) view.queueFeedback = this.queueFeedback;
    if (magickCd) view.magickCd = magickCd;
    const held = this.charging?.spec ?? this.releasing?.spec;
    if (held && held.kind === 'projectile') view.charging = { charge: view.charge, spec: held };
    // Deeper spells: the stash, the weave the last two pieces make, a weave or Grand Magick in progress.
    const stash = this.prediction.stash;
    view.stash = stash;
    view.weaveNext = weaveTarget(queue);
    if (snap.wv) view.weaving = { el: snap.wv[0], f: snap.wv[1] };
    const grand = matchGrand(queue, stash);
    if (snap.gi) view.grand = { id: snap.gi[0], f: snap.gi[1], cd: snap.gc ?? 0 };
    else if (grand) view.grand = { id: grand.id, cd: snap.gc ?? 0 };
    if (grand) view.grandSlots = grand.slots;
    if (pos) {
      view.x = pos.x;
      view.y = pos.y;
    }
    return view;
  }
}
